import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Typography } from '@/components/ui';
import { cn } from '@/lib/utils';

// Base error state props
interface BaseErrorProps extends HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Empty State Error - For page/section load failures
 * Use when: Page data fails to load, major feature unavailable
 */
export interface EmptyStateErrorProps extends BaseErrorProps {
  illustration?: ReactNode;
  secondaryAction?: ReactNode;
}

export const EmptyStateError = forwardRef<HTMLDivElement, EmptyStateErrorProps>(
  (
    {
      title = 'Unable to load content',
      description = "We're having trouble loading this content right now.",
      illustration,
      action,
      secondaryAction,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center min-h-[400px] p-8 text-center',
          className
        )}
        {...props}
      >
        {/* Illustration or default icon */}
        <div className="mb-6">
          {illustration || (
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto">
              <svg
                className="w-8 h-8 text-error-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <Typography variant="heading-white-center" className="mb-2 max-w-md">
          {title}
        </Typography>

        {/* Description */}
        <Typography variant="body-gray-center" className="mb-6 max-w-md">
          {description}
        </Typography>

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className="flex flex-col sm:flex-row gap-3">
            {action}
            {secondaryAction}
          </div>
        )}
      </div>
    );
  }
);

EmptyStateError.displayName = 'EmptyStateError';

/**
 * Inline Error Card - For component-level failures
 * Use when: Individual component fails, widget unavailable
 */
export interface InlineErrorCardProps extends BaseErrorProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'minimal' | 'outlined' | 'filled';
}

export const InlineErrorCard = forwardRef<HTMLDivElement, InlineErrorCardProps>(
  (
    {
      title = 'Component Error',
      description = 'This component is temporarily unavailable.',
      icon,
      action,
      size = 'md',
      variant = 'outlined',
      className,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'p-3 text-sm',
      md: 'p-4',
      lg: 'p-6',
    };

    const variantClasses = {
      minimal: 'bg-error-50 border-l-4 border-error-400',
      outlined: 'bg-error-50 border border-error-200 rounded-lg',
      filled: 'bg-error-100 rounded-lg',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start space-x-3',
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
        {...props}
      >
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {icon || (
            <svg
              className="w-5 h-5 text-error-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <Typography variant="body-primary" className="font-medium mb-1">
            {title}
          </Typography>
          {description && (
            <Typography variant="body-gray-2" className="text-sm mb-2">
              {description}
            </Typography>
          )}
          {action && <div className="mt-2">{action}</div>}
        </div>
      </div>
    );
  }
);

InlineErrorCard.displayName = 'InlineErrorCard';

/**
 * Field Error - For form validation errors
 * Use when: Form field validation fails
 */
export interface FieldErrorProps extends HTMLAttributes<HTMLDivElement> {
  message: string;
  className?: string;
}

export const FieldError = forwardRef<HTMLDivElement, FieldErrorProps>(
  ({ message, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center mt-1 text-sm text-error-600',
          className
        )}
        {...props}
      >
        <svg
          className="w-4 h-4 mr-1 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>{message}</span>
      </div>
    );
  }
);

FieldError.displayName = 'FieldError';

/**
 * Banner Notification - For system-wide issues
 * Use when: Network offline, maintenance mode, system alerts
 */
export interface BannerNotificationProps
  extends HTMLAttributes<HTMLDivElement> {
  message: string;
  type?: 'error' | 'warning' | 'info';
  dismissible?: boolean;
  onDismiss?: () => void;
  action?: ReactNode;
  className?: string;
}

export const BannerNotification = forwardRef<
  HTMLDivElement,
  BannerNotificationProps
>(
  (
    {
      message,
      type = 'error',
      dismissible = false,
      onDismiss,
      action,
      className,
      ...props
    },
    ref
  ) => {
    const typeClasses = {
      error: 'bg-error-600 text-white',
      warning: 'bg-warning-500 text-white',
      info: 'bg-blue-600 text-white',
    };

    const typeIcons = {
      error: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      warning: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
          />
        </svg>
      ),
      info: (
        <svg
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    };

    return (
      <div
        ref={ref}
        className={cn('relative px-4 py-3', typeClasses[type], className)}
        {...props}
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Content */}
          <div className="flex items-center space-x-3 flex-1">
            <div className="flex-shrink-0">{typeIcons[type]}</div>
            <span className="font-medium">{message}</span>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {action && <div>{action}</div>}

            {dismissible && onDismiss && (
              <button
                onClick={onDismiss}
                className="flex-shrink-0 p-1 hover:bg-black/10 rounded transition-colors"
                aria-label="Dismiss notification"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
);

BannerNotification.displayName = 'BannerNotification';

/**
 * Loading Error State - For failed loading states
 * Use when: Data loading fails but page structure remains
 */
export interface LoadingErrorProps extends BaseErrorProps {
  isLoading?: boolean;
  onRetry?: () => void;
  retryLabel?: string;
}

export const LoadingError = forwardRef<HTMLDivElement, LoadingErrorProps>(
  (
    {
      title = 'Failed to load',
      description = 'Unable to load this content.',
      isLoading = false,
      onRetry,
      retryLabel = 'Try again',
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center p-8 text-center min-h-[200px]',
          className
        )}
        {...props}
      >
        <div className="w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-error-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <Typography variant="body-primary" className="font-medium mb-1">
          {title}
        </Typography>

        <Typography variant="body-gray-2" className="text-sm mb-4 max-w-xs">
          {description}
        </Typography>

        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isLoading}
            className={cn(
              'px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium',
              'hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            {isLoading ? 'Loading...' : retryLabel}
          </button>
        )}
      </div>
    );
  }
);

LoadingError.displayName = 'LoadingError';
